import { arrayx, stringx } from "@deathbycode/fp-ts-ext"
import { LocaleD } from "@prisma/client"
import categoryView from "app/analytics/mutations/categoryView"
import { SlugPathAndPageC } from "app/categories/codecs/SlugPathAndPage"
import { CategoryDetails, CategoryDetailsProps } from "app/categories/components/CategoryDetails"
import { NortherPageProps } from "app/common/components/NortherPageProps"
import { decodeWithTE, decodeWithTE2 } from "app/common/core/decode"
import { InvalidPageNumberError } from "app/common/errors/InvalidPageNumberError"
import { joinWithSlash } from "app/common/fp-ts/array"
import { logException } from "app/common/logging"
import { bindCommon } from "app/common/next"
import { UrlMgr } from "app/common/routes"
import { createSeoMetadata } from "app/common/seo/seoEssentials"
import { getServerSidePropsResult, notFoundOrThrowR } from "app/common/ui/next"
import { createBrandSearchClient } from "app/common/utilities/azure/search"
import { getPaginationModel } from "app/common/utilities/paging"
import { selectId } from "app/common/utilities/types"
import { toTitleString } from "app/common/web"
import { BRAND_LIST_PAGE_SIZE } from "app/data/brands/brand.queries"
import { getCategoriesBySlugPath, getCategoryArticles } from "app/data/categories/category.queries"
import BlogLayout from "app/layouts/BlogLayout"
import { createSearchParams, DelimitedFiltersC } from "app/search/codecs"
import { createCategoryFilter, isNotCategoryFilter } from "app/search/codecs/StringEqualsFilterC"
import { searchBrands } from "app/search/performSearch"
import { BlitzPage, GetServerSideProps, useMutation } from "blitz"
import {
  apply,
  array,
  either,
  identity,
  nonEmptyArray,
  option,
  readerTask,
  readerTaskEither,
  taskEither,
} from "fp-ts"
import { pipe } from "fp-ts/function"
import * as t from "io-ts"
import { optionFromNullable } from "io-ts-types"
import React, { useEffect } from "react"
// const mapLocales = <T,>(fn: (locale: LocaleD) => T): T[] => pipe(Object.values(LocaleD), array.map(fn))

// const emptyLocalePath = (locale: LocaleD) => ({
//   locale,
//   slugPath: [] as string[],
// })

// export const getStaticPaths: GetStaticPaths = () =>
//   pipe(
//     getCategoryLocalizeds(),
//     taskEither.map(
//       flow(
//         array.concat(mapLocales(emptyLocalePath)),
//         array.map(({ locale, slugPath }) => ({
//           locale: localeToCanadianLocale(locale),
//           params: {
//             slugPathAndPage: slugPath,
//           },
//         })),
//         getStaticPathsResult
//       )
//     ),
//     taskEither.mapLeft(logException),
//     taskEither.fold(justThrow, task.of)
//   )()

// export const getStaticPaths: GetStaticPaths = () => Promise.resolve(getEmptyStaticPathsResult())

type CategoryPageProps = NortherPageProps & CategoryDetailsProps & { categories: { id: number }[] }

const CategoryParamsC = t.strict({
  filter: optionFromNullable(DelimitedFiltersC),
})

// ts-prune-ignore-next
export const getServerSideProps: GetServerSideProps<CategoryPageProps> = ({
  locale,
  params,
  query,
}) =>
  pipe(
    taskEither.Do,
    bindCommon(locale),
    taskEither.apS("slugPathAndPage", decodeWithTE2(SlugPathAndPageC)(params?.slugPathAndPage)),
    taskEither.bind("slugPath", ({ slugPathAndPage: { slugPath } }) => taskEither.of(slugPath)),
    taskEither.bind("page", ({ slugPathAndPage: { page } }) => taskEither.of(page)),
    taskEither.apS("parameters", pipe(query, decodeWithTE(CategoryParamsC))),
    taskEither.bind("filters", ({ parameters, slugPath }) =>
      pipe(
        apply.sequenceT(option.Applicative)(
          pipe(
            parameters.filter,
            option.map(array.filter(isNotCategoryFilter)),
            option.alt(() => option.some([]))
          ),
          pipe(
            slugPath,
            joinWithSlash,
            createCategoryFilter,
            array.fromPredicate(({ value }) => stringx.isNonEmpty(value)),
            option.some
          )
        ),
        option.map(array.flatten),
        option.getOrElse(() => []),
        taskEither.of
      )
    ),
    taskEither.bind("searchParams", ({ filters, locale, page }) =>
      pipe(
        {
          filter: filters,
          locale,
          orderBy: option.some(array.of(`${locale}/name`)),
          page,
          pageSize: BRAND_LIST_PAGE_SIZE,
          q: "*",
        },
        createSearchParams,
        taskEither.of
      )
    ),
    taskEither.bind("categoryMap", ({ slugPath }) => getCategoriesBySlugPath(slugPath)),
    taskEither.bind("categories", ({ categoryMap, locale }) => taskEither.of(categoryMap[locale])),
    taskEither.bind("articles", ({ categories, locale }) =>
      pipe(categories, array.map(selectId), getCategoryArticles(locale))
    ),
    readerTaskEither.fromTaskEither,
    readerTaskEither.bindW("brands", ({ searchParams }) => pipe(searchParams, searchBrands)),
    readerTaskEither.bindW("paginationModel", ({ brands: { pagination } }) =>
      pipe(
        pagination,
        either.fromPredicate(
          ({ page, totalPages }) => page <= totalPages,
          ({ page }) => InvalidPageNumberError.of(page)
        ),
        either.map(getPaginationModel),
        readerTaskEither.fromEither
      )
    ),
    readerTaskEither.bindW(
      "seoMetadata",
      ({ categories, categoryMap: { en_ca, fr_ca }, locale, ogLocale, page, t }) =>
        pipe(
          identity.Do,
          identity.apS(
            "firstCategory",
            arrayx.isSingle(categories) ? array.head(categories) : option.none
          ),
          identity.bind("namePath", ({ firstCategory }) =>
            pipe(
              firstCategory,
              option.map((cat) => cat.namePath),
              option.getOrElse(() => [])
            )
          ),
          identity.map(({ firstCategory, namePath }) =>
            pipe({
              canonicalUrl: UrlMgr.CategoryPage({
                locale,
                slugPath: pipe(
                  firstCategory,
                  option.map((cat) => cat.slugPath),
                  option.getOrElse(() => [])
                ),
              }),
              openGraphMetadata: {
                description: arrayx.isSingle(categories)
                  ? `Discover all the brands listed on Norther under the category: ${namePath.join(
                      " > "
                    )}`
                  : `Discover all the brands listed on Norther by category`,
                locale: ogLocale,
                siteName: "Norther",
                title: pipe(
                  namePath,
                  array.reverse,
                  array.append(t("categories")),
                  array.append(t("buy-local-buy-canadian")),
                  array.append(`Norther`),
                  toTitleString
                ),
                type: option.none,
                url: UrlMgr.CategoryPage({
                  locale,
                  slugPath: arrayx.isSingle(en_ca) ? en_ca[0].slugPath : [],
                }),
              },
              title: pipe(
                namePath,
                array.reverse,
                array.append(t("categories")),
                array.append(t("buy-local-buy-canadian")),
                array.append(`Norther`),
                toTitleString
              ),
              urlMap: {
                en_ca: UrlMgr.CategoryPage({
                  page,
                  slugPath: arrayx.isSingle(en_ca) ? en_ca[0].slugPath : [],
                }),
                fr_ca: UrlMgr.CategoryPage({
                  locale: LocaleD.fr_ca,
                  page,
                  slugPath: arrayx.isSingle(fr_ca) ? fr_ca[0].slugPath : [],
                }),
                "x-default": UrlMgr.CategoryPage({
                  page,
                  slugPath: arrayx.isSingle(en_ca) ? en_ca[0].slugPath : [],
                }),
              },
            })
          ),
          createSeoMetadata,
          readerTaskEither.of
        )
    ),
    readerTaskEither.map(
      ({
        articles,
        brands,
        categories,
        filters,
        locale,
        paginationModel,
        seoMetadata,
        slugPath,
      }) => ({
        articles,
        brands,
        categories,
        filters,
        locale,
        paginationModel,
        seoMetadata,
        slugPath,
      })
    ),
    readerTaskEither.bimap(logException, getServerSidePropsResult),
    readerTaskEither.fold(notFoundOrThrowR, readerTask.of)
  )(createBrandSearchClient())()

const CategoryPage: BlitzPage<CategoryPageProps> = (props) => {
  const { categories, locale } = props

  const [createCategoryView] = useMutation(categoryView)

  useEffect(() => {
    if (arrayx.isSingle(categories)) {
      pipe(categories, nonEmptyArray.head, ({ id }) =>
        createCategoryView({
          id,
          locale,
        })
      )
    }
  }, [createCategoryView, categories, locale])

  return <CategoryDetails {...props} />
}

CategoryPage.suppressFirstRenderFlicker = true

CategoryPage.getLayout = BlogLayout.getLayout

// ts-prune-ignore-next
export default CategoryPage
